/**
 * Register photo metadata in chunks as files land, instead of once at the end.
 *
 * The old flow awaited Promise.all over the whole batch and then made a single
 * metadata call. A tab killed mid-batch -- backgrounded app, in-app browser, OS
 * reclaim -- meant that call never fired and EVERY photo was orphaned, including
 * the ones whose bytes were already sitting in storage. All 367 zero-photo
 * orders with an upload group in a 14-day window had zero photo rows.
 *
 * Chunking, plus a page-hide beacon that carries BOTH the still-queued items and
 * every batch the chain has claimed but not yet confirmed, is what bounds that
 * loss. See flushOnHide() for exactly what is and is not guaranteed.
 *
 * Retries here are only safe because the endpoint is idempotent: the backend
 * inserts with ON CONFLICT DO NOTHING against UQ_photo_group_rank_key_live,
 * a partial unique index on (order_photo_id, rank, key) over live rows.
 *
 * A retry repeats both rank and key, so it collides and does nothing. A
 * customer's deliberate second copy of the same image repeats the key at a
 * different rank, and a different photo at the same rank carries a different
 * key -- so neither is mistaken for a retry. See createPhotosMetadata in
 * photo.service.ts for the production counts that ruled out the narrower
 * two-column forms. Do not ship this without that index.
 */

/**
 * Files per flush.
 *
 * The cost is 5.5x more calls to uploadMetadataBatch (3,494 -> 19,304 per 30
 * days), which is ~640/day against 122,000 for GET /order/:id alone --
 * irrelevant at this volume. The endpoint does no pricing work (p50 24ms), so
 * this does not touch recalculation.
 *
 * Note this size does NOT by itself bound the worst-case loss. Uploads can
 * outrun registration on a slow link, so several full chunks can sit claimed in
 * the serialised chain at once. What bounds the loss is flushOnHide() beaconing
 * those claimed-but-unconfirmed batches as well as `pending`.
 */
export const METADATA_CHUNK_SIZE = 10;

export interface PhotoMetadata {
  key: string;
  type: string;
  name: string;
  count: number;
  rank: number;
  orderId: string;
  orderPhotosId: string;
}

export interface MetadataFlusher {
  /** Queue one landed photo. Never blocks; starts a flush when a chunk fills. */
  add(item: PhotoMetadata): void;
  /**
   * Send everything still queued, retrying one transient failure.
   *
   * Resolves with the number of items this flusher has permanently given up on
   * (see `drain`'s implementation note). A non-zero result means those photos
   * are in storage but were never registered, so the caller MUST surface it --
   * the customer would otherwise be shown a successful upload for photos that
   * will never be printed.
   */
  drain(): Promise<number>;
  /**
   * Synchronously hand everything unregistered to the beacon. Called when the
   * page is being hidden or torn down, where an async request would simply be
   * cancelled.
   */
  flushOnHide(): void;
  pendingCount(): number;
}

/**
 * A 400 means the order is no longer cart-mutable. Retrying would loop.
 *
 * Exported so callers can classify the error they are handed in `onError`
 * without re-deriving the rule: a terminal 400 is an expected refusal and
 * belongs at `warning`, while any other permanent failure is a real loss and
 * belongs at `error`.
 */
export function isTerminal(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status === 400;
}

export function createMetadataFlusher(opts: {
  send: (batch: PhotoMetadata[]) => Promise<void>;
  chunkSize?: number;
  onError?: (error: unknown) => void;
  /**
   * Escape hatch for page-hide: hands the queue off synchronously instead of
   * going through `send`/the chain. See flushOnHide().
   *
   * May return a promise. If that promise rejects, the items this call claimed
   * out of `pending` are put back so a surviving page can still send them.
   */
  beacon?: (batch: PhotoMetadata[]) => void | Promise<void>;
}): MetadataFlusher {
  const chunkSize = opts.chunkSize ?? METADATA_CHUNK_SIZE;
  const pending: PhotoMetadata[] = [];
  /**
   * Beacons fired by flushOnHide that have not settled yet.
   *
   * drain() must await these: a failed beacon requeues its batch onto
   * `pending`, and a drain that spliced first would report success over photos
   * nobody will ever send.
   */
  const beaconsInFlight: Promise<void>[] = [];
  // Batches spliced out of `pending` and handed to the chain, but NOT yet
  // confirmed by a resolved `send`. Kept in claim order.
  //
  // This exists because `pending` alone is not "everything unregistered".
  // add() claims a full chunk synchronously and hands it to the serialised
  // chain, so on a slow link several chunks can be queued behind an in-flight
  // request at once -- 30-50 photos, not the chunkSize-1 the old comment here
  // claimed. Those batches travel over ordinary axios with no keepalive, so the
  // browser cancels them outright when the tab goes away. Without tracking them
  // they were unreachable from flushOnHide() and simply lost.
  //
  // Re-sending a batch that was actually in flight is harmless: the endpoint is
  // idempotent, which is precisely what Part A bought.
  const unconfirmed: PhotoMetadata[][] = [];
  // Items this flusher has permanently given up on. Reported out of drain().
  let droppedCount = 0;
  // Serialises sends: batches are handed to `send` in the order they were
  // claimed, and we never open N parallel requests. Never rejects -- a
  // failed send is handled inside sendBatch, so the chain always resolves.
  let chain: Promise<void> = Promise.resolve();

  const claim = (batch: PhotoMetadata[]): void => {
    if (batch.length > 0) unconfirmed.push(batch);
  };

  // Identity-based: the exact array object handed to claim(). Called on every
  // terminal outcome (sent, refused, given up, or handed back to `pending`),
  // so a batch is never owned by two places at once.
  const release = (batch: PhotoMetadata[]): void => {
    const at = unconfirmed.indexOf(batch);
    if (at !== -1) unconfirmed.splice(at, 1);
  };

  // A caller's error reporter must never poison the chain: if opts.onError
  // itself threw, that exception would escape sendBatch and reject `chain`.
  // A rejected chain makes every future `chain.then(cb)` skip `cb` and
  // propagate the rejection instead -- so no send() would ever run again,
  // pending items would still get spliced out (and so LOOK flushed via
  // pendingCount()) but never sent or requeued, and every photo added after
  // that point would be silently lost for the rest of the flusher's life.
  const reportError = (error: unknown): void => {
    try {
      opts.onError?.(error);
    } catch {
      // Swallowed deliberately -- see comment above.
    }
  };

  const sendBatch = async (
    batch: PhotoMetadata[],
    retryOnFailure: boolean
  ): Promise<void> => {
    if (batch.length === 0) return;

    try {
      await opts.send(batch);
      // Confirmed registered. Only now does it stop being the beacon's problem.
      release(batch);
    } catch (error) {
      if (isTerminal(error)) {
        // Drop it. The order cannot accept writes any more, and the customer is
        // past the point where this batch could matter.
        release(batch);
        reportError(error);
        return;
      }
      if (retryOnFailure) {
        // Back to the FRONT, so rank order is preserved for the retry in
        // drain(). Ownership moves from the chain back to `pending`.
        release(batch);
        pending.unshift(...batch);
        return;
      }
      // Given up on for good. These photos exist in storage and will never be
      // registered, so count them: drain() reports the total to the caller,
      // which is the only thing standing between this and a UI that claims a
      // successful upload for photos that will never be printed.
      release(batch);
      droppedCount += batch.length;
      reportError(error);
    }
  };

  // Chains the send for an already-claimed batch onto the queue. The batch
  // must be claimed (spliced out of `pending`) by the CALLER before this
  // runs -- `.then()` callbacks never fire synchronously, so claiming here
  // instead would let a burst of synchronous add() calls (or add() followed
  // immediately by drain()) all see the same still-full `pending` and race
  // to grab it, merging what should be separate chunks into one and leaving
  // pendingCount() stale until the chain gets around to running.
  const enqueueSend = (batch: PhotoMetadata[], retryOnFailure: boolean): Promise<void> => {
    claim(batch);
    chain = chain.then(() => sendBatch(batch, retryOnFailure));
    return chain;
  };

  return {
    add(item: PhotoMetadata): void {
      pending.push(item);
      if (pending.length >= chunkSize) {
        // Claim the chunk now, synchronously, so pendingCount() reflects it
        // immediately and this call's items can't be swept into a later
        // flush's batch.
        const batch = pending.splice(0, chunkSize);
        // Deliberately not awaited: uploads continue while this is in flight.
        void enqueueSend(batch, true);
      }
    },

    async drain(): Promise<number> {
      // A beacon fired by flushOnHide may still be in flight, and on failure it
      // puts its batch BACK onto `pending`. If we spliced before that landed we
      // would see an empty queue, report success, and the requeued photos would
      // never be sent by anyone -- this flusher is function-local and its
      // listeners are removed as soon as the upload returns. Bytes in storage,
      // no rows, and droppedCount still 0 so nothing alerts.
      //
      // Happens on an ordinary app-switch mid-upload: visibilitychange fires,
      // the customer comes back, the last files finish, and the beacon's 401 or
      // timeout arrives after drain() has already resolved.
      while (beaconsInFlight.length > 0) {
        const settling = beaconsInFlight.splice(0, beaconsInFlight.length);
        // Each already routes its own failure through requeue(); awaiting is
        // only to be sure that has happened before we look at `pending`.
        await Promise.all(settling);
      }

      // First pass: claim whatever remains and send it, retrying once on a
      // transient failure (which unshifts it back onto `pending`).
      const batch = pending.splice(0, pending.length);
      await enqueueSend(batch, true);
      // Second pass: claim whatever came back from a failed first attempt
      // and send it one more time, giving up for good on failure.
      const retryBatch = pending.splice(0, pending.length);
      await enqueueSend(retryBatch, false);
      // Awaiting the chain above means every batch ever queued has now settled,
      // so this total is final. Only the give-up branch contributes: a terminal
      // 400 is a deliberate refusal by an order that is no longer cart-mutable,
      // and alerting a customer who has already checked out would be noise.
      return droppedCount;
    },

    flushOnHide(): void {
      if (!opts.beacon) return;
      // `pending` is genuinely claimed here -- moved out, so a photo can never
      // be sent by both the beacon and a later chained send.
      //
      // `unconfirmed` is COPIED, not moved: those batches are still owned by
      // the chain, which will release them when their send settles. If the page
      // turns out to survive (visibilitychange fires on any ordinary app
      // switch), the chain still completes them normally. If it does not, the
      // beacon is the only thing that will have carried them. Sending both is
      // safe because the endpoint is idempotent.
      const carried = unconfirmed.flat();
      const claimed = pending.splice(0, pending.length);
      const batch = [...carried, ...claimed];
      if (batch.length === 0) return;

      // If the beacon fails -- a 401, offline, a preflight that was never
      // cached -- the items we just claimed must go back, or drain() on a page
      // that did not actually close can never see them again and they are lost
      // for good. Pre-chunking, the end-of-batch call would have sent them.
      // When the page really is closing this costs nothing.
      const requeue = () => {
        if (claimed.length > 0) pending.unshift(...claimed);
      };
      try {
        // Tracked, so drain() can await it. Failure is folded into a RESOLVED
        // promise: the requeue is the handling, and a rejection here would
        // otherwise surface as an unhandled rejection on a page that survived.
        beaconsInFlight.push(
          Promise.resolve(opts.beacon(batch)).then(() => {}, requeue),
        );
      } catch {
        // A beacon that throws synchronously (unsupported keepalive, torn-down
        // context) is the same loss as one that rejects.
        requeue();
      }
    },

    pendingCount(): number {
      return pending.length;
    },
  };
}
